(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scrollBehavior = () => reducedMotion.matches ? 'instant' : 'smooth';

  class AtelierElement extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.setup();
    }
    listen(target, event, callback, options = {}) {
      target?.addEventListener(event, callback, { ...options, signal: this.abort.signal });
    }
    disconnectedCallback() {
      this.abort?.abort();
      clearInterval(this.timer);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }
  }

  class AtelierHeader extends AtelierElement {
    setup() {
      this.update = () => {
        this.classList.toggle('is-scrolled', this.dataset.sticky === 'true' && window.scrollY > 160);
      };
      this.listen(window, 'scroll', this.update, { passive: true });
      this.update();
      this.listen(this, 'click', (event) => {
        const dropdown = event.target.closest('.ak-desktop-nav .ak-nav-dropdown');
        if (!dropdown) return;
        this.querySelectorAll('.ak-desktop-nav details[open]').forEach(other => {
          if (other !== dropdown) other.open = false;
        });
      });
      this.listen(document, 'click', (event) => {
        if (!this.contains(event.target)) this.closeMenus();
      });
      this.listen(this, 'keydown', (event) => {
        if (event.key === 'Escape') {
          const menu = event.target.closest('.ak-nav-dropdown');
          if (menu) {
            menu.open = false;
            menu.querySelector('summary').focus();
          }
        }
      });
      this.querySelectorAll('.ak-desktop-nav .ak-nav-dropdown').forEach(menu => {
        this.listen(menu, 'pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.closeMenus();
          menu.open = true;
        });
        this.listen(menu, 'pointerleave', () => {
          if (!menu.contains(document.activeElement)) menu.open = false;
        });
        this.listen(menu, 'focusout', (event) => {
          if (!menu.contains(event.relatedTarget)) menu.open = false;
        });
      });
    }
    closeMenus() {
      this.querySelectorAll('.ak-desktop-nav details[open]').forEach(menu => { menu.open = false; });
    }
  }

  class AtelierMenu extends AtelierElement {
    setup() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.summary.setAttribute('role', 'button');
      this.summary.setAttribute('aria-expanded', 'false');
      this.listen(this.summary, 'click', (event) => {
        event.preventDefault();
        this.details.open ? this.close() : this.open();
      });
      this.listen(this.querySelector('button'), 'click', () => this.close());
      this.listen(this.querySelector('.modal-overlay'), 'click', () => this.close());
      this.listen(this, 'keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
      });
      this.listen(window, 'resize', () => {
        if (window.innerWidth >= 990 && this.details.open) this.close();
      });
    }
    open() {
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      trapFocus(this.querySelector('[tabindex="-1"]'), this.querySelector('button'));
    }
    close() {
      if (!this.details.open) return;
      this.details.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.summary);
    }
    disconnectedCallback() {
      this.close();
      super.disconnectedCallback();
    }
  }

  class AtelierAnnouncement extends AtelierElement {
    setup() {
      this.messages = [...this.querySelectorAll('[data-message]')];
      this.index = 0;
      this.listen(this.querySelector('[data-prev]'), 'click', () => this.show(this.index - 1));
      this.listen(this.querySelector('[data-next]'), 'click', () => this.show(this.index + 1));
      if (this.messages.length > 1 && !reducedMotion.matches) {
        this.timer = setInterval(() => {
          if (!document.hidden && !this.matches(':hover') && !this.contains(document.activeElement)) this.show(this.index + 1);
        }, Number(this.dataset.interval));
      }
    }
    show(index) {
      if (!this.messages.length) return;
      this.index = (index + this.messages.length) % this.messages.length;
      this.messages.forEach((message, i) => { message.hidden = i !== this.index; });
    }
  }

  class AtelierSlideshow extends AtelierElement {
    setup() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.index = 0;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.visible = typeof IntersectionObserver === 'undefined';
      const firstImage = this.querySelector('img');
      this.firstImageReady = !firstImage || firstImage.complete;
      this.listen(firstImage, 'load', () => {
        this.firstImageReady = true;
        this.warmNextSlide();
      }, { once: true });
      if (typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.warmNextSlide();
        }, { threshold: .1 });
        this.observer.observe(this);
      }
      this.warmNextSlide();
      this.listen(this.querySelector('[data-prev]'), 'click', () => this.show(this.index - 1));
      this.listen(this.querySelector('[data-next]'), 'click', () => this.show(this.index + 1));
      this.dots.forEach((dot, i) => this.listen(dot, 'click', () => this.show(i)));
      this.listen(this.querySelector('[data-pause]'), 'click', () => {
        this.paused = !this.paused;
        this.updatePauseButton();
      });
      this.listen(this, 'pointerenter', () => { this.hovered = true; });
      this.listen(this, 'pointerleave', () => { this.hovered = false; });
      this.listen(this, 'focusin', () => { this.focused = true; });
      this.listen(this, 'focusout', (event) => { this.focused = this.contains(event.relatedTarget); });
      this.listen(this, 'pointerdown', (event) => {
        this.swiped = false;
        this.pointerStart = { x: event.clientX, y: event.clientY };
      }, { passive: true });
      this.listen(this, 'pointerup', (event) => {
        if (!this.pointerStart) return;
        const dx = event.clientX - this.pointerStart.x;
        const dy = event.clientY - this.pointerStart.y;
        if (Math.abs(dx) > 60 && Math.abs(dy) < 70) {
          this.show(this.index + (dx > 0 ? -1 : 1));
          this.swiped = true;
        }
        this.pointerStart = null;
      }, { passive: true });
      this.listen(this, 'click', (event) => {
        if (this.swiped) {
          event.preventDefault();
          this.swiped = false;
        }
      }, { capture: true });
      this.listen(this, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index >= 0) {
          this.paused = true;
          this.show(index);
          this.updatePauseButton();
        }
      });
      if (this.dataset.autoplay === 'true' && this.slides.length > 1) {
        this.timer = setInterval(() => {
          if (this.visible && this.firstImageReady && !reducedMotion.matches && !this.paused && !this.hovered && !this.focused && !document.hidden) this.show(this.index + 1);
        }, Number(this.dataset.interval));
      }
    }
    warmNextSlide() {
      if (!this.nextSlideWarmed && this.visible && this.firstImageReady && !reducedMotion.matches) {
        this.nextSlideWarmed = true;
        this.loadSlide(1);
      }
    }
    show(index) {
      if (!this.slides.length) return;
      const next = (index + this.slides.length) % this.slides.length;
      const request = this.slideRequest = (this.slideRequest || 0) + 1;
      this.loadSlide(next);
      this.loadSlide(next + 1);
      const image = this.slides[next].querySelector('img');
      const activate = () => {
        if (this.isConnected && this.slideRequest === request) this.activateSlide(next);
      };
      if (image && !image.complete) image.decode().catch(() => {}).then(activate);
      else activate();
    }
    activateSlide(index) {
      this.index = index;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        this.dots[i]?.setAttribute('aria-pressed', String(active));
      });
    }
    loadSlide(index) {
      if (!this.slides.length) return;
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      const template = slide.querySelector('template[data-slide-media]');
      if (!template) return;
      const media = template.content.cloneNode(true);
      media.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      template.replaceWith(media);
    }
    updatePauseButton() {
      const button = this.querySelector('[data-pause]');
      if (!button) return;
      button.setAttribute('aria-pressed', String(this.paused));
      button.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
      button.querySelector('span').textContent = this.paused ? '▶' : 'Ⅱ';
    }
  }

  class AtelierArrivals extends AtelierElement {
    setup() {
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.index = 0;
      this.tabs.forEach((tab, i) => {
        this.listen(tab, 'click', () => this.select(i));
        this.listen(tab, 'keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            this.select(next);
            this.tabs[next].focus();
          }
        });
      });
      this.listen(this.querySelector('[data-prev]'), 'click', () => this.move(-1));
      this.listen(this.querySelector('[data-next]'), 'click', () => this.move(1));
      this.querySelectorAll('[data-track]').forEach(track => {
        this.listen(track, 'scroll', () => this.updateControls(), { passive: true });
      });
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this);
      this.listen(this, 'shopify:block:select', (event) => {
        const tab = event.target.closest('[data-tab]');
        if (tab) this.select(Number(tab.dataset.tab));
      });
      this.updateControls();
    }
    get track() { return this.panels[this.index]?.querySelector('[data-track]'); }
    select(index) {
      this.index = index;
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
      this.updateControls();
    }
    move(direction) {
      const track = this.track;
      if (!track) return;
      const card = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      const width = card?.getBoundingClientRect().width || track.clientWidth;
      const visible = Math.max(1, Math.round(track.clientWidth / (width + gap)));
      track.scrollBy({ left: direction * visible * (width + gap), behavior: scrollBehavior() });
    }
    updateControls() {
      const track = this.track;
      if (!track) return;
      this.querySelector('[data-prev]').disabled = track.scrollLeft <= 2;
      this.querySelector('[data-next]').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }
  }

  class AtelierFilm extends AtelierElement {
    setup() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-video-toggle]');
      if (!this.video) {
        this.button.hidden = true;
        return;
      }
      this.userPaused = false;
      this.listen(this.button, 'click', () => {
        this.userPaused = !this.video.paused;
        if (this.video.paused) this.video.play().catch(() => {});
        else this.video.pause();
      });
      const update = () => {
        this.button.setAttribute('aria-label', this.video.paused ? 'Play film' : 'Pause film');
        this.button.setAttribute('aria-pressed', String(!this.video.paused));
        this.button.querySelector('span').textContent = this.video.paused ? '▶' : 'Ⅱ';
      };
      this.listen(this.video, 'play', update);
      this.listen(this.video, 'pause', update);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.syncPlayback();
      }, { threshold: .15 });
      this.observer.observe(this);
      this.listen(document, 'visibilitychange', () => this.syncPlayback());
      this.listen(reducedMotion, 'change', () => this.syncPlayback());
    }
    syncPlayback() {
      if (this.visible && !document.hidden && !reducedMotion.matches && !this.userPaused) this.video.play().catch(() => {});
      else this.video.pause();
    }
    disconnectedCallback() {
      this.video?.pause();
      super.disconnectedCallback();
    }
  }

  class AtelierMuse extends AtelierElement {
    setup() {
      this.dialog = this.querySelector('dialog');
      this.video = this.dialog.querySelector('video');
      this.listen(this, 'click', (event) => {
        const item = event.target.closest('[data-video]');
        if (!item || !item.dataset.video) return;
        this.trigger = item;
        this.video.src = item.dataset.video;
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
        this.video.play().catch(() => {});
      });
      this.listen(this.querySelector('[data-close]'), 'click', () => this.dialog.close());
      this.listen(this.dialog, 'click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.listen(this.dialog, 'close', () => this.cleanup());
    }
    cleanup() {
      this.video?.pause();
      this.video?.removeAttribute('src');
      this.video?.load();
      document.body.classList.remove('overflow-hidden');
      this.trigger?.focus();
    }
    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        this.cleanup();
      }
      super.disconnectedCallback();
    }
  }

  const elements = {
    'ak-header': AtelierHeader,
    'ak-menu': AtelierMenu,
    'ak-announcement': AtelierAnnouncement,
    'ak-slideshow': AtelierSlideshow,
    'ak-arrivals': AtelierArrivals,
    'ak-film': AtelierFilm,
    'ak-muse': AtelierMuse,
  };
  Object.entries(elements).forEach(([name, component]) => {
    if (!customElements.get(name)) customElements.define(name, component);
  });

  const initialized = new WeakSet();
  const bindControls = () => {
    document.querySelectorAll('[data-ak-country], [data-back-to-top]').forEach(control => {
      if (initialized.has(control)) return;
      initialized.add(control);
      if (control.matches('[data-ak-country]')) control.addEventListener('change', () => control.form.requestSubmit());
      else control.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: scrollBehavior() });
        document.querySelector('.ak-header__logo')?.focus({ preventScroll: true });
      });
    });
  };
  bindControls();
  document.addEventListener('shopify:section:load', bindControls);
  window.addEventListener('scroll', () => {
    document.querySelectorAll('[data-back-to-top]').forEach(button => { button.hidden = window.scrollY < 600; });
  }, { passive: true });
})();
